/**
 * useScreener – Stock Screener store (Zustand).
 *
 * A full scan takes >60 s on the backend, so it no longer runs inline:
 * run() POSTs /run (background compute) then polls GET /results until the
 * fresh payload lands. fetchStored() loads the last stored result instantly
 * on page mount — it NEVER triggers a scan.
 */
import { create } from 'zustand'
import { runScreener, getScreenerResults, getScreenerSectors, getScreenerPresets } from '@/lib/api'
import type { ScreenerFilters, ScreenerStock, ScreenerPreset } from '@/lib/types'

interface ScreenerState {
  results: ScreenerStock[]
  count: number
  heldCount: number
  sectors: string[]
  presets: ScreenerPreset[]
  filters: ScreenerFilters
  loading: boolean
  error: string | null
  sortField: string
  sortDir: 'asc' | 'desc'
  /** Honest timestamp of the stored result being displayed. */
  asOf: string | null
  /** True while a background run is in flight. */
  running: boolean
  /** False until the first run has ever completed — show "press Run". */
  hasRun: boolean
  note: string | null

  setFilters: (filters: Partial<ScreenerFilters>) => void
  resetFilters: () => void
  applyPreset: (presetId: string) => void
  setSortField: (field: string) => void
  run: () => Promise<void>
  fetchStored: () => Promise<void>
  fetchSectors: () => Promise<void>
  fetchPresets: () => Promise<void>
}

const DEFAULT_FILTERS: ScreenerFilters = {}

const POLL_INTERVAL_MS = 2_500
const MAX_POLLS = 60 // ~2.5 min safety cap, matches backend run budget

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export const useScreener = create<ScreenerState>()((set, get) => ({
  results: [],
  count: 0,
  heldCount: 0,
  sectors: [],
  presets: [],
  filters: { ...DEFAULT_FILTERS },
  loading: false,
  error: null,
  sortField: 'market_cap_m',
  sortDir: 'desc',
  asOf: null,
  running: false,
  hasRun: false,
  note: null,

  setFilters: (partial) => {
    set(s => ({ filters: { ...s.filters, ...partial } }))
  },

  resetFilters: () => {
    set({ filters: { ...DEFAULT_FILTERS } })
  },

  applyPreset: (presetId) => {
    set({ filters: { preset: presetId } })
    get().run()
  },

  setSortField: (field) => {
    const { sortField, sortDir } = get()
    if (field === sortField) {
      set({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' })
    } else {
      set({ sortField: field, sortDir: 'desc' })
    }
  },

  /** Load the last stored result instantly. Never starts a scan. */
  fetchStored: async () => {
    try {
      const res = await getScreenerResults()
      set({
        results: res.results ?? [],
        count: res.count ?? 0,
        heldCount: res.held_count ?? 0,
        asOf: res.as_of ?? null,
        running: res.running ?? false,
        hasRun: res.status === 'ready',
        note: res.note ?? null,
      })
    } catch (e) {
      set({ error: String(e) })
    }
  },

  /** Kick a background run, then poll until the fresh result lands. */
  run: async () => {
    set({ loading: true, running: true, error: null })
    try {
      const prevAsOf = get().asOf
      await runScreener(get().filters)
      for (let i = 0; i < MAX_POLLS; i++) {
        await sleep(POLL_INTERVAL_MS)
        const res = await getScreenerResults()
        const done = !(res.running ?? false)
        const fresh = (res.as_of ?? null) !== prevAsOf
        if (done || fresh) {
          set({
            results: res.results ?? [],
            count: res.count ?? 0,
            heldCount: res.held_count ?? 0,
            asOf: res.as_of ?? null,
            running: res.running ?? false,
            hasRun: res.status === 'ready',
            note: res.note ?? null,
            loading: false,
          })
        }
        if (done) return
      }
      // Safety cap hit — surface honestly rather than spinning forever
      set({ loading: false, running: false, error: 'Screener run is taking longer than expected — refresh in a minute.' })
    } catch (e) {
      set({ error: String(e), loading: false, running: false })
    }
  },

  fetchSectors: async () => {
    try {
      const { sectors } = await getScreenerSectors()
      set({ sectors })
    } catch (e) {
      set({ error: String(e) })
    }
  },

  fetchPresets: async () => {
    try {
      const { presets } = await getScreenerPresets()
      set({ presets })
    } catch (e) {
      set({ error: String(e) })
    }
  },
}))
